import React, { useEffect, useCallback, useState } from "react";
import { connect } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";

import {
  EMPTY_STRING,
  userConstants,
  agentConstants,
  profileSections,
  adminReduxConstants,
  ELIGIBILITY_OPTIONS,
} from "../../constants";
import { ERROR_TOAST } from "../../constants/toast.constants";
import {
  userProfileSchema,
  agentProfileSchema,
  feedbackSchema,
} from "../../schemas";
import { IMAGE_TYPES, APPROVAL_STATUS } from "../../system/global-enums";
import {
  customToast,
  hasAgentAccess,
  areArraysEqual,
  objectToFormData,
} from "../../shared/utility";
import MainSpinner from "../../shared/components/spinner/MainSpinner";
import RoundedButton from "../../shared/components/buttons/RoundedButton";
import ConfirmationPopup from "../../shared/components/modals/ConfirmationPopup";
import { UseNavigatingAway } from "../../shared/hooks/use-navigating-away/UseNavigatingAway";
import MainContentWrapper from "../../shared/components/main-content-wrapper/MainContentWrapper";
import {
  homeActions,
  adminActions,
  agentActions,
  countryAction,
  systemActions,
} from "../../redux/actions";
import { userActions } from "../../redux/actions/user.actions";
import Topbar from "../../layout/Topbar";
import AgentFiles from "./AgentFiles";
import Preferences from "./Preferences";
import UserFeedback from "./UserFeedback";
import PasswordField from "./PasswordField";
import CompanyDetails from "./CompanyDetails";
import PersonalDetails from "./PersonalDetails";
import EligibleCountryModal from "./EligibleCountryModal";

const { USER_PROFILE } = userConstants;
const { AGENT_DETAILS } = agentConstants;
const { GET_AGENTS } = adminReduxConstants;

const defaultEditState = {
  passwords: false,
  agentFiles: false,
  preferences: false,
  userFeedback: false,
  companyDetails: false,
  personalDetails: false,
};

const MyProfile = (props) => {
  const {
    user: { userId },
    userRole,
    userProfile,
    agentDetails,
    allLanguages,
    allCurrencies,
    spinnerActions,
    saveChanges,
    sendFeedback,
    getAllHobbies,
    getUserProfile,
    getAgentDetails,
    getAllLanguages,
    getAllCurrencies,
    getAllDayToDayActivity,
    updateUserProfile,
    updateAgentProfile,
    allCountries,
    getAllCountries,
    setSaveChanges,
    reSubmitApprovalRequest,
    updateDefaultCurrency,
  } = props;

  const [isEditing, setIsEditing] = useState(defaultEditState);
  const [allowedCountries, setAllowedCountries] = useState([]);
  const [deletedImageIds, setDeletedImageIds] = useState([]);
  const [eligibleCountryModal, setEligibleCountryModal] = useState(false);
  const [userChoice, setUserChoice] = useState([]);

  const [showDialogPrompt, confirmNavigation, cancelNavigation] =
    UseNavigatingAway(saveChanges);

  const openEligibleCountryModal = () => {
    setEligibleCountryModal(true);
  };

  const closeEligibleCountryModal = () => {
    setEligibleCountryModal(false);
  };

  const profileMethods = useForm({
    mode: "onChange",
    resolver: yupResolver(
      hasAgentAccess(userRole) ? agentProfileSchema : userProfileSchema
    ),
  });
  const feedbackMethods = useForm({
    mode: "onChange",
    resolver: yupResolver(feedbackSchema),
  });

  const { reset, setValue, getValues } = profileMethods;

  const spinner = useCallback(
    (type) => spinnerActions.findIndex((s) => s === type),
    // eslint-disable-next-line
    [spinnerActions]
  );

  const updateSuccessFunc = (response) => {
    if (response?.needsConfirmation) {
      setValue("isEligibleToBuy", ELIGIBILITY_OPTIONS.YES);
      setAllowedCountries(response.eligibleCountries);
      setUserChoice(getValues("countriesLikeToLiveIds"));
      openEligibleCountryModal();
    } else {
      setIsEditing(defaultEditState);
      if (hasAgentAccess(userRole)) {
        getAgentDetails(userId);
      } else {
        getUserProfile(userId);
      }
      closeEligibleCountryModal();
      setAllowedCountries([]);
    }
  };
  const handleChangeEditor = (type, open) => {
    setIsEditing({
      ...defaultEditState,
      [type]: !!open,
    });
    if (hasAgentAccess(userRole)) {
      if (Object.entries(agentDetails).length > 0) {
        reset({
          agent: {
            firstName: agentDetails.firstName,
            lastName: agentDetails.lastName,
            id: agentDetails.id,
            phoneNumber: agentDetails.phoneNumber,
            email: agentDetails.email,
            languageSpokenIds: agentDetails.languageSpokenIds,
            preferredContactMethod: agentDetails.preferredContactMethod,
            emergencyContactInfo: {
              fullName: agentDetails.emergencyContactInfo?.fullName || "",
              phoneNumber: agentDetails.emergencyContactInfo?.phoneNumber || "",
            },
          },
          company: { ...agentDetails.company },
          files: agentDetails?.images?.filter(
            (img) => img.imageType === IMAGE_TYPES.TYPE_PHOTO_ID
          ),
          headshotfiles: agentDetails?.images?.filter(
            (img) => img.imageType === IMAGE_TYPES.TYPE_HEADSHOT
          ),
          document: agentDetails?.images?.filter(
            (img) => img.imageType === IMAGE_TYPES.TYPE_DOCUMENT
          ),
        });
        setDeletedImageIds([]);
      }
    } else {
      if (Object.entries(userProfile).length > 0) {
        reset(userProfile);
      }
    }
    if (type === profileSections.userFeedback && !open) {
      resetUserFeedbackField();
    }
  };

  const getSelectedCountries = () => {
    let selectedIds = [];
    const countriesLikeToLive = getValues("countriesLikeToLiveIds");

    if (getValues("isEligibleToBuy") === ELIGIBILITY_OPTIONS.YES) {
      selectedIds = countriesLikeToLive;
    }
    if (getValues("isEligibleToBuy") === ELIGIBILITY_OPTIONS.NO) {
      selectedIds = countriesLikeToLive.filter((id) =>
        allowedCountries.includes(id)
      );
    }

    return selectedIds;
  };

  const onSubmit = (data) => {
    if (hasAgentAccess(userRole)) {
      let tempData = {
        agent: data.agent,
        company: data.company,
        deletedImageIds,
      };
      const photoId = data.files.filter((file) => !file.id);
      const image = data.headshotfiles.filter((file) => !file.id);
      const document = data.document.filter((file) => !file.id);

      const formData = objectToFormData(tempData);

      for (let i = 0; i < photoId.length; i++) {
        formData.append(`photoId${i + 1}`, photoId[i]);
      }

      for (let i = 0; i < image.length; i++) {
        formData.append(`image`, image[i]);
      }

      for (let i = 0; i < document.length; i++) {
        formData.append(`document`, document[i]);
      }

      updateAgentProfile(formData, updateSuccessFunc);
    } else {
      const selectedCountries = getSelectedCountries();
      if (
        eligibleCountryModal &&
        (selectedCountries === null || selectedCountries.length === 0)
      ) {
        customToast(
          "Please select at least one country you'd like to live in.",
          ERROR_TOAST
        );
        return;
      }
      updateUserProfile(
        {
          ...data,
          ownsHouse: Number(data.ownsHouse) === 1 || data.ownsHouse === true,
          interestedCitizenshipId: data.interestedCitizenshipId ?? 0,
          ...(eligibleCountryModal
            ? {
                countriesLikeToLiveIds: selectedCountries,
              }
            : {}),
          ...(eligibleCountryModal ||
          (areArraysEqual(
            data.countriesLikeToLiveIds,
            userProfile.countriesLikeToLiveIds
          ) &&
            areArraysEqual(data.passportHold, userProfile.passportHold))
            ? { isConfirm: true }
            : {}),
        },
        (response) => {
          if (data.defaultCurrencyId !== userProfile.defaultCurrencyId) {
            updateDefaultCurrency(data.defaultCurrencyId);
          }
          updateSuccessFunc(response);
        }
      );
    }
  };

  const resetUserFeedbackField = () => {
    feedbackMethods.setValue("userFeedback", EMPTY_STRING);
    setIsEditing(defaultEditState);
  };

  const updateFeedback = (data) => {
    const dataToSend = {
      id: userId,
      feedback: data.userFeedback,
    };
    sendFeedback(dataToSend, resetUserFeedbackField);
  };

  useEffect(() => {
    if (hasAgentAccess(userRole)) {
      if (Object.entries(agentDetails).length > 0) {
        reset({
          agent: {
            firstName: agentDetails.firstName,
            lastName: agentDetails.lastName,
            id: agentDetails.id,
            phoneNumber: agentDetails.phoneNumber,
            email: agentDetails.email,
            languageSpokenIds: agentDetails.languageSpokenIds,
            preferredContactMethod: agentDetails.preferredContactMethod,
            emergencyContactInfo: {
              fullName: agentDetails.emergencyContactInfo?.fullName || "",
              phoneNumber: agentDetails.emergencyContactInfo?.phoneNumber || "",
            },
          },
          company: { ...agentDetails.company },
          files: agentDetails?.images?.filter(
            (img) => img.imageType === IMAGE_TYPES.TYPE_PHOTO_ID
          ),
          headshotfiles: agentDetails?.images?.filter(
            (img) => img.imageType === IMAGE_TYPES.TYPE_HEADSHOT
          ),
          document: agentDetails?.images?.filter(
            (img) => img.imageType === IMAGE_TYPES.TYPE_DOCUMENT
          ),
        });
        setDeletedImageIds([]);
      }
    } else {
      if (Object.entries(userProfile).length > 0) {
        reset(userProfile);
      }
    }

    // eslint-disable-next-line
  }, [userProfile, agentDetails]);

  useEffect(() => {
    if (hasAgentAccess(userRole)) {
      getAgentDetails(userId);
      getAllLanguages();
    } else {
      getUserProfile(userId);
      getAllHobbies();
      getAllCurrencies();
      getAllDayToDayActivity();
    }
    getAllCountries();

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setSaveChanges(Object.values(isEditing).some((val) => !!val));
    // eslint-disable-next-line
  }, [isEditing]);

  return (
    <>
      <Topbar />
      {(spinner(USER_PROFILE) > -1 ||
        spinner(AGENT_DETAILS) > -1 ||
        spinner(GET_AGENTS) > -1) && <MainSpinner />}
      {showDialogPrompt && (
        <ConfirmationPopup
          onCancel={cancelNavigation}
          onConfirm={() => {
            confirmNavigation();
            setSaveChanges(false);
          }}
        />
      )}
      <div className="user-profile-parallex">
        <MainContentWrapper>
          <div className="container parallex-content">
            <div className="mt-5">
              {agentDetails?.approvalStatus === APPROVAL_STATUS.PENDING && (
                <div className="w-100 approval-pending">
                  Your request to become an agent is under review. Please wait for approval before accessing the system further.
                </div>
              )}
              {agentDetails?.approvalStatus === APPROVAL_STATUS.REJECTED && (
                <RoundedButton
                  cotainerClass="w-100 d-flex justify-content-end"
                  className="py-2 px-3"
                  onClick={() =>
                    reSubmitApprovalRequest(userId, updateSuccessFunc)
                  }
                >
                  Re-submit approval request
                </RoundedButton>
              )}
              <FormProvider {...profileMethods}>
                <form onSubmit={profileMethods.handleSubmit(onSubmit)}>
                  <EligibleCountryModal
                    show={eligibleCountryModal}
                    onClose={closeEligibleCountryModal}
                    userChoice={userChoice}
                    allowedCountries={allowedCountries}
                    onSubmit={() => {
                      profileMethods.handleSubmit(onSubmit)();
                    }}
                  />
                  <PersonalDetails
                    isAgent={hasAgentAccess(userRole)}
                    isEditing={isEditing.personalDetails}
                    handleChangeEditor={(open) =>
                      handleChangeEditor(profileSections.personalDetails, open)
                    }
                    allLanguages={allLanguages}
                    allCountries={allCountries}
                    onCurrencyChange={(id) => setValue("defaultCurrencyId", id)}
                    defaultCurrency={getValues("defaultCurrencyId")}
                    allCurrencies={allCurrencies}
                  />
                  {hasAgentAccess(userRole) && (
                    <>
                      <CompanyDetails
                        isEditing={isEditing.companyDetails}
                        handleChangeEditor={(open) =>
                          handleChangeEditor(
                            profileSections.companyDetails,
                            open
                          )
                        }
                      />
                      <AgentFiles
                        isEditing={isEditing.agentFiles}
                        handleChangeEditor={(open) =>
                          handleChangeEditor(profileSections.agentFiles, open)
                        }
                        setDeletedImageIds={setDeletedImageIds}
                      />
                    </>
                  )}
                  {!hasAgentAccess(userRole) && (
                    <Preferences
                      isEditing={isEditing.preferences}
                      handleChangeEditor={(open) =>
                        handleChangeEditor(profileSections.preferences, open)
                      }
                      allCurrencies={allCurrencies}
                    />
                  )}
                  <PasswordField
                    isAgent={hasAgentAccess(userRole)}
                    isEditing={isEditing.passwords}
                    handleChangeEditor={(open) =>
                      handleChangeEditor(profileSections.passwords, open)
                    }
                  />
                </form>
              </FormProvider>
              {!hasAgentAccess(userRole) && (
                <FormProvider {...feedbackMethods}>
                  <form onSubmit={feedbackMethods.handleSubmit(updateFeedback)}>
                    <UserFeedback
                      isEditing={isEditing.userFeedback}
                      handleChangeEditor={(open) =>
                        handleChangeEditor(profileSections.userFeedback, open)
                      }
                    />
                  </form>
                </FormProvider>
              )}
            </div>
          </div>
        </MainContentWrapper>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  user: state.authReducer.user,
  userRole: state.authReducer.userRole,
  userProfile: state.userReducer.user,
  agentDetails: state.adminReducer.agentDetails,
  allCurrencies: state.homeReducer.allCurrencies,
  allCountries: state.countryReducer.allCountries,
  allLanguages: state.countryReducer.allLanguages,
  spinnerActions: state.systemReducer.spinnerActions,
  saveChanges: state.systemReducer.saveChanges,
});

const mapDispatchToProps = (dispatch) => ({
  getAllHobbies: () => dispatch(homeActions.getAllHobbies()),
  getUserProfile: (id) => dispatch(userActions.getUserProfile(id)),
  getAllLanguages: () => dispatch(countryAction.getAllLanguages()),
  getAllCountries: () => dispatch(countryAction.getAllCountries()),
  getAllCurrencies: () => dispatch(homeActions.getAllCurrencies()),
  getAllDayToDayActivity: () => dispatch(homeActions.getAllDayToDayActivity()),
  updateUserProfile: (data, func) =>
    dispatch(userActions.updateUserProfile(data, func)),
  getAgentDetails: (id) => dispatch(adminActions.getAgentDetails(id)),
  updateAgentProfile: (data, func) =>
    dispatch(agentActions.updateAgentProfile(data, func)),
  sendFeedback: (data, onSuccess) =>
    dispatch(userActions.sendFeedback(data, onSuccess)),
  reSubmitApprovalRequest: (id, onSuccess) =>
    dispatch(agentActions.reSubmitApprovalRequest(id, onSuccess)),
  updateDefaultCurrency: (id) =>
    dispatch(homeActions.updateDefaultCurrency(id)),
  setSaveChanges: (flag) => dispatch(systemActions.setSaveChanges(flag)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MyProfile);
